import { useGetMarketingListingQuery } from '@/store/api/services/marketing/marketing';
import type { ListingView } from '@/types';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import { Badge, Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import ListingMediaLightbox from './ListingMediaLightbox';

const THUMBNAIL_SIZE = 48;

interface ListingMediaCellProps {
  listing: ListingView;
}

const ListingMediaCell = ({ listing }: ListingMediaCellProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasRowImages = listing.image_urls !== undefined;
  const { data: detail, isLoading } = useGetMarketingListingQuery(listing.location_id, {
    skip: hasRowImages,
  });
  const images = (hasRowImages ? listing.image_urls : detail?.data?.image_urls) ?? [];
  const [first] = images;
  const title = listing.name ?? `Location ${listing.location_id}`;

  if (!hasRowImages && isLoading) {
    return <Skeleton variant="rounded" width={THUMBNAIL_SIZE} height={THUMBNAIL_SIZE} />;
  }

  if (!first) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        color="text.disabled"
        data-test="listing-media-empty"
      >
        <HideImageOutlinedIcon fontSize="small" />
        <Typography variant="caption">No media</Typography>
      </Stack>
    );
  }

  return (
    <>
      <Tooltip title={`View ${images.length} ${images.length === 1 ? 'image' : 'images'}`}>
        <Badge
          badgeContent={images.length > 1 ? images.length : 0}
          color="primary"
          overlap="rectangular"
        >
          <Box
            component="button"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setIsOpen(true);
            }}
            data-test="listing-media-thumbnail"
            sx={{
              p: 0,
              width: THUMBNAIL_SIZE,
              height: THUMBNAIL_SIZE,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'zoom-in',
              bgcolor: 'action.hover',
            }}
          >
            <Box
              component="img"
              src={first.url}
              alt={title}
              loading="lazy"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: `rotate(${first.rotation ?? 0}deg)`,
              }}
            />
          </Box>
        </Badge>
      </Tooltip>
      <ListingMediaLightbox
        open={isOpen}
        onClose={() => setIsOpen(false)}
        locationId={listing.location_id}
        title={title}
        images={images}
      />
    </>
  );
};

export default ListingMediaCell;
